"use client"

import {
  Alert,
  Box,
  Button,
  Dialog,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  Portal,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useTransitionRouter } from "next-view-transitions"
import { FC, useCallback, useEffect, useMemo, useReducer, useRef } from "react"

import siteConfig from "@/config/siteConfig"
import { getAllPosts } from "@/lib"
import { Category_id, Post, Skill_id } from "@/types/database"

import SearchItem from "./SearchItem"
import SearchResultCard from "./SearchResultCard"
import { Option, Props } from "./TSearchModal"
import {
  BackspaceIcon,
  CloseOutlineIcon,
  InfoIcon,
  SearchOutlineIcon,
} from "@/components/icons"

interface SearchState {
  posts: Post[]
  inputValue: string
  isLoading: boolean
  highlightedIndex: number
  hasSearched: boolean
}

type SearchAction =
  | { type: "RESET" }
  | { type: "SET_INPUT"; value: string }
  | { type: "SEARCH_START" }
  | { type: "SEARCH_COMPLETE"; posts: Post[] }
  | { type: "CLEAR_RESULTS" }
  | { type: "HIGHLIGHT_NEXT"; totalItems: number }
  | { type: "HIGHLIGHT_PREV" }

const initialSearchState: SearchState = {
  hasSearched: false,
  highlightedIndex: -1,
  inputValue: "",
  isLoading: false,
  posts: [],
}

function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case "RESET":
      return initialSearchState
    case "SET_INPUT":
      return { ...state, highlightedIndex: -1, inputValue: action.value }
    case "SEARCH_START":
      return { ...state, hasSearched: false, isLoading: true }
    case "SEARCH_COMPLETE":
      return {
        ...state,
        hasSearched: true,
        isLoading: false,
        posts: action.posts,
      }
    case "CLEAR_RESULTS":
      return { ...state, hasSearched: false, posts: [] }
    case "HIGHLIGHT_NEXT":
      return {
        ...state,
        highlightedIndex:
          state.highlightedIndex < action.totalItems - 1
            ? state.highlightedIndex + 1
            : state.highlightedIndex,
      }
    case "HIGHLIGHT_PREV":
      return {
        ...state,
        highlightedIndex:
          state.highlightedIndex > 0 ? state.highlightedIndex - 1 : -1,
      }
  }
}

const SearchModal: FC<Props> = ({ open, setOpen }) => {
  const router = useTransitionRouter()
  const [
    { hasSearched, highlightedIndex, inputValue, isLoading, posts },
    dispatch,
  ] = useReducer(searchReducer, initialSearchState)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const recommended_searches = siteConfig.recommended_searches
  const quick_actions = siteConfig.quick_actions
  const isComboboxOpen = open

  const clearSearchTimeout = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = undefined
    }
  }, [])

  const resetModalState = useCallback(() => {
    clearSearchTimeout()
    dispatch({ type: "RESET" })
  }, [clearSearchTimeout])

  const closeModal = useCallback(() => {
    resetModalState()
    setOpen(false)
  }, [resetModalState, setOpen])

  const setInputElement = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node
      if (node && open) {
        setTimeout(() => {
          node.focus()
        }, 100)
      }
    },
    [open]
  )

  useEffect(() => {
    if (!open) {
      document.body.style.overflow = ""
      return
    }

    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  // Debounced search function
  const searchPosts = useCallback(async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      dispatch({ type: "CLEAR_RESULTS" })
      return
    }

    dispatch({ type: "SEARCH_START" })
    try {
      const response = await getAllPosts({ limit: 100 }) // Get more posts for search

      // Handle the GraphQL response structure
      if (response && response.posts && Array.isArray(response.posts)) {
        const allPosts = response.posts
        const filteredPosts = allPosts.filter(
          (post: Post) =>
            post?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post?.headline?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post?.author?.name
              ?.toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            post.skills?.some((skill: Skill_id) =>
              skill?.skills_id?.name
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
            ) ||
            post.category?.some((cat: Category_id) =>
              cat?.category_id?.title
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
            )
        )
        dispatch({
          posts: filteredPosts.slice(0, 10),
          type: "SEARCH_COMPLETE",
        })
      } else {
        console.warn("No posts found or invalid response structure:", response)
        dispatch({ posts: [], type: "SEARCH_COMPLETE" })
      }
    } catch (error) {
      console.error("Error searching posts:", error)
      dispatch({ posts: [], type: "SEARCH_COMPLETE" })
    }
  }, [])

  // Handle input value changes with debouncing
  const handleInputChange = useCallback(
    (value: string) => {
      dispatch({ type: "SET_INPUT", value })

      // Clear existing timeout
      clearSearchTimeout()

      // Set new timeout for debounced search
      searchTimeoutRef.current = setTimeout(() => {
        searchPosts(value)
      }, 300) // 300ms delay
    },
    [clearSearchTimeout, searchPosts]
  )

  const announcement = useMemo(() => {
    if (isLoading) {
      return "Searching…"
    }
    if (posts.length > 0) {
      return `${posts.length} search results found. Use arrow keys to navigate, Enter to select.`
    }
    if (hasSearched && inputValue.trim() && posts.length === 0) {
      return "No search results found."
    }
    return ""
  }, [posts.length, isLoading, inputValue, hasSearched])

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Calculate what items are actually being rendered
      const hasPosts = !isLoading && posts.length > 0
      const hasRecommendedSearches =
        !isLoading && posts.length === 0 && recommended_searches.length > 0
      const hasQuickActions = quick_actions.length > 0

      // Calculate total items based on what's actually rendered
      let totalItems = 0
      if (hasPosts) totalItems += posts.length
      if (hasRecommendedSearches) totalItems += recommended_searches.length
      if (hasQuickActions) totalItems += quick_actions.length

      if (e.key === "ArrowDown") {
        e.preventDefault()
        dispatch({ totalItems, type: "HIGHLIGHT_NEXT" })
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        dispatch({ type: "HIGHLIGHT_PREV" })
      } else if (e.key === "Tab") {
        // Allow Tab to work normally for accessibility
        // Don't prevent default - let it cycle through focusable elements
        return
      } else if (e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault()

        // Determine which list the highlighted item belongs to
        let selectedItem: Option | Post | null = null
        let currentIndex = 0

        // Check posts first
        if (hasPosts) {
          if (highlightedIndex < posts.length) {
            const post = posts[highlightedIndex]
            if (post) {
              selectedItem = post
            }
          } else {
            currentIndex += posts.length
          }
        }

        // Check recommended searches
        if (!selectedItem && hasRecommendedSearches) {
          if (
            highlightedIndex >= currentIndex &&
            highlightedIndex < currentIndex + recommended_searches.length
          ) {
            const recommendedIndex = highlightedIndex - currentIndex
            const recommendedSearch = recommended_searches[recommendedIndex]
            if (recommendedSearch) {
              selectedItem = recommendedSearch
            }
          } else {
            currentIndex += recommended_searches.length
          }
        }

        // Check quick actions
        if (!selectedItem && hasQuickActions) {
          if (
            highlightedIndex >= currentIndex &&
            highlightedIndex < currentIndex + quick_actions.length
          ) {
            const quickActionIndex = highlightedIndex - currentIndex
            const quickAction = quick_actions[quickActionIndex]
            if (quickAction) {
              selectedItem = quickAction
            }
          }
        }

        if (selectedItem) {
          if ("uri" in selectedItem) {
            const uri =
              selectedItem.type === "recommended_searches"
                ? selectedItem.uri
                : selectedItem.uri + inputValue
            router.push(uri, { transitionTypes: ["nav-forward"] })
          } else {
            router.push(`/article/${(selectedItem as Post).slug}`, {
              transitionTypes: ["nav-forward"],
            })
          }
          closeModal()
        }
      } else if (e.key === "Escape") {
        e.preventDefault()
        closeModal()
      }
    },
    [
      closeModal,
      posts,
      quick_actions,
      recommended_searches,
      highlightedIndex,
      inputValue,
      router,
      isLoading,
    ]
  )

  // Handle item selection
  const handleItemClick = useCallback(
    (item: Option | Post) => {
      if ("uri" in item) {
        const uri =
          item.type === "recommended_searches"
            ? item.uri
            : item.uri + inputValue
        router.push(uri, { transitionTypes: ["nav-forward"] })
      } else {
        router.push(`/article/${(item as Post).slug}`, {
          transitionTypes: ["nav-forward"],
        })
      }
      closeModal()
    },
    [closeModal, inputValue, router]
  )

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      clearSearchTimeout()
    }
  }, [clearSearchTimeout])

  const endElement = inputValue ? (
    <IconButton
      aria-label="Clear search"
      colorPalette="gray"
      onClick={() => {
        resetModalState()
        inputRef.current?.focus()
      }}
      p="2"
      rounded="xl"
      size="lg"
      variant="ghost"
    >
      <Icon as={BackspaceIcon} color="fg.muted" size={"md"} />
    </IconButton>
  ) : undefined

  return (
    <>
      {/* Screen reader announcements */}
      <Box
        aria-atomic="true"
        aria-live="polite"
        height="1px"
        left="-10000px"
        overflow="hidden"
        position="absolute"
        width="1px"
      >
        {announcement}
      </Box>

      <Dialog.Root
        closeOnEscape={true}
        closeOnInteractOutside={true}
        lazyMount
        motionPreset="slide-in-bottom"
        onOpenChange={({ open: nextOpen }) => {
          if (nextOpen) {
            setOpen(true)
            return
          }

          closeModal()
        }}
        open={open}
        placement="top"
        scrollBehavior="outside"
        size="xl"
        unmountOnExit={true}
      >
        <Portal>
          <Dialog.Backdrop backdropFilter="blur(10px)" bg="blackAlpha.700" />
          <Dialog.Positioner p={6} top="100px">
            <Dialog.Content borderRadius="2xl" overflow="hidden">
              <Dialog.Title srOnly>Search</Dialog.Title>
              <Dialog.Description srOnly>
                Search for articles, categories, authors, and skills. Use
                keyboard navigation to browse results.
              </Dialog.Description>
              <Dialog.CloseTrigger asChild>
                <Button
                  aria-label="Close search"
                  colorPalette="gray"
                  p="2"
                  position="absolute"
                  right={2}
                  rounded="xl"
                  size="sm"
                  top={"10px"}
                  variant="ghost"
                  zIndex={10}
                >
                  <Text display={{ base: "none", lg: "block" }} fontSize="xs">
                    Esc
                  </Text>
                  <Icon display={{ base: "block", lg: "none" }} size={"md"}>
                    <CloseOutlineIcon />
                  </Icon>
                </Button>
              </Dialog.CloseTrigger>
              <Dialog.Body p={0}>
                <HStack>
                  <InputGroup
                    _focus={{
                      borderColor: "fg.muted",
                      borderWidth: "0px",
                      outline: "none",
                    }}
                    borderBottom="1px solid"
                    borderColor="border"
                    borderRadius="0"
                    endElement={endElement}
                    endElementProps={{
                      right: "38px",
                    }}
                    startElement={
                      <Icon color={"fg.muted"} size={"lg"}>
                        <SearchOutlineIcon />
                      </Icon>
                    }
                  >
                    <Input
                      _focus={{
                        borderColor: "fg.muted",
                        borderWidth: "0px",
                        outline: "none",
                      }}
                      _focusVisible={{
                        borderColor: "fg.muted",
                        borderWidth: "0px",
                        outline: "none",
                      }}
                      aria-activedescendant={
                        highlightedIndex >= 0
                          ? `search-item-${highlightedIndex}`
                          : undefined
                      }
                      aria-autocomplete="list"
                      aria-controls="search-results"
                      aria-describedby="search-instructions"
                      aria-expanded={isComboboxOpen}
                      aria-label="Search for articles, categories, and authors"
                      borderRadius="0"
                      borderWidth="0px"
                      fontSize="lg"
                      height="52px"
                      marginRight={4}
                      onChange={(e) => handleInputChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type to search..."
                      ref={setInputElement}
                      role="combobox"
                      size="lg"
                      value={inputValue}
                    />
                  </InputGroup>
                </HStack>

                {/* Keyboard navigation instructions for screen readers */}
                <Box aria-hidden="true" id="search-instructions" srOnly>
                  Use arrow keys to navigate search results, Enter to select,
                  Escape to close, Tab to move between elements.
                </Box>

                {isComboboxOpen && (
                  <VStack
                    alignItems="stretch"
                    aria-label="Search results"
                    gap={0}
                    id="search-results"
                    maxH="60vh"
                    overflowY="auto"
                    role="listbox"
                  >
                    <VStack alignItems="stretch" gap={0}>
                      {isLoading && inputValue.trim() && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          key={`loading-${inputValue.trim()}`}
                          p={4}
                        >
                          {/* TODO: Add a skeleton animation */}
                          <Spinner size="sm" />
                          <Text color="gray.500" fontSize="sm" ml={2}>
                            Searching…
                          </Text>
                        </Box>
                      )}
                      {!isLoading &&
                        hasSearched &&
                        posts.length === 0 &&
                        inputValue.trim() && (
                          <Box key={`no-results-${inputValue.trim()}`} p={4}>
                            <Alert.Root status="info" title="No results">
                              <Alert.Indicator>
                                <InfoIcon />
                              </Alert.Indicator>
                              <Alert.Content color="fg">
                                <Alert.Title>
                                  No posts found matching{" "}
                                  <Text as="span" fontWeight="bold">
                                    "{inputValue.trim()}"
                                  </Text>
                                </Alert.Title>
                                <Alert.Description>
                                  Try searching with different keywords or
                                  browse our categories
                                </Alert.Description>
                              </Alert.Content>
                            </Alert.Root>
                          </Box>
                        )}
                      {!isLoading &&
                        posts.length === 0 &&
                        recommended_searches.length > 0 && (
                          <VStack
                            alignItems={"stretch"}
                            gap={0}
                            p={0}
                            px={3}
                            py={2}
                          >
                            <Text
                              color="fg.muted"
                              fontSize="xs"
                              id="recommended-searches-label"
                              pb={1}
                              pl={3}
                              pt={4}
                            >
                              Recommended Searches
                            </Text>
                            {recommended_searches.map((item, index) => (
                              <SearchItem
                                handleItemClick={() => handleItemClick(item)}
                                highlightedIndex={highlightedIndex}
                                index={index}
                                item={item}
                                key={`${item.type}-${item.uri}`}
                              />
                            ))}
                          </VStack>
                        )}

                      {!isLoading &&
                        posts.length > 0 &&
                        posts.map((item, index) => (
                          <SearchResultCard
                            index={index}
                            isHighlighted={highlightedIndex === index}
                            key={`post-${item.slug}`}
                            onClick={() => handleItemClick(item)}
                            post={item as Post}
                          />
                        ))}

                      {quick_actions.length > 0 && (
                        <VStack
                          alignItems={"stretch"}
                          gap={0}
                          p={0}
                          px={3}
                          py={2}
                        >
                          <Text
                            color="fg.muted"
                            fontSize="xs"
                            id="quick-actions-label"
                            pb={1}
                            pl={3}
                            pt={4}
                          >
                            Quick Actions
                          </Text>
                          {quick_actions.map((item, index) => {
                            // Calculate global index for quick actions
                            const globalIndex = (() => {
                              let globalIdx = 0
                              if (!isLoading && posts.length > 0) {
                                globalIdx += posts.length
                              } else if (
                                !isLoading &&
                                posts.length === 0 &&
                                recommended_searches.length > 0
                              ) {
                                globalIdx += recommended_searches.length
                              }
                              return globalIdx + index
                            })()

                            return (
                              <SearchItem
                                handleItemClick={() => handleItemClick(item)}
                                highlightedIndex={highlightedIndex}
                                index={globalIndex}
                                item={item}
                                key={`${item.type}-${item.uri}`}
                              />
                            )
                          })}
                        </VStack>
                      )}
                    </VStack>
                  </VStack>
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}

export default SearchModal
